import React, { useState } from "react";
import { Card, Button, Table, Space, Modal, message, Badge, Dropdown } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DownOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useLoadList, useDelete, useTableSelection } from "../../../hooks";
import { masterDataService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import AircraftFilter from "./components/AircraftFilter";
import AircraftForm from "./components/AircraftForm";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import AircraftTag from "../../../components/AircraftTag";
import AircraftAvailabilityWindowDrawer from "./components/AircraftAvailabilityWindowDrawer";

const { confirm } = Modal;

const AircraftList = () => {
    // 获取权限相关功能
    const { hasPermission, hasAnyPermission } = useAuth();
    const canReadAvailabilityWindows = hasAnyPermission(["masterdata:read", "schedule:read"]);
    const canWriteAvailabilityWindows = hasAnyPermission(["masterdata:update", "schedule:update"]);
    const canDeleteAvailabilityWindows = hasAnyPermission(["masterdata:delete", "schedule:delete"]);

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
    });
    const [formVisible, setFormVisible] = useState(false);
    const [currentAircraft, setCurrentAircraft] = useState(null);
    const [formMode, setFormMode] = useState("add"); // 'add' 或 'edit'
    const [availabilityDrawerOpen, setAvailabilityDrawerOpen] = useState(false);
    const [availabilityAircraft, setAvailabilityAircraft] = useState(null);

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("aircraft", masterDataService.aircraft.getAircraftList, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    // 使用自定义Hook处理表格选择
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection } = useTableSelection({
        rowKey: "registration",
        dataSource: data?.items || [],
        pagination,
    });

    // 删除飞机的Hook
    const deleteMutation = useDelete("aircraft", masterDataService.deleteAircraft, {
        onSuccess: () => {
            message.success("删除成功");
            refetch();
        },
    });

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "注册号",
            dataIndex: "registration",
            key: "registration",
            width: 120,
            render: (text) => <AircraftTag registration={text} />,
        },
        {
            title: "机型",
            dataIndex: "aircraft_type",
            key: "aircraft_type",
            width: 100,
        },
        {
            title: "序列号",
            dataIndex: "serial_number",
            key: "serial_number",
            width: 150,
        },
        {
            title: "最大起飞重量(吨)",
            dataIndex: "mtow",
            key: "mtow",
            width: 150,
        },
        {
            title: "航程(公里)",
            dataIndex: "range_km",
            key: "range_km",
            width: 120,
        },
        {
            title: "航班号偏移量",
            dataIndex: "flight_number_offset",
            key: "flight_number_offset",
            width: 120,
        },
        {
            title: "可用开始日期",
            dataIndex: "availability_start",
            key: "availability_start",
            width: 150,
            render: (text) => formatDateTime(text, "YYYY-MM-DD"),
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status) => <Badge className="status-badge" status={status === "ACTIVE" ? "success" : "error"} text={status === "ACTIVE" ? "启用" : "停用"} />,
        },
        {
            title: "备注",
            dataIndex: "remarks",
            key: "remarks",
            ellipsis: true,
            width: 150,
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 180,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 230,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    {canReadAvailabilityWindows && (
                        <Button type="link" size="small" icon={<ClockCircleOutlined />} onClick={() => handleOpenAvailabilityDrawer(record)}>
                            可用性
                        </Button>
                    )}
                    <PermissionGuard permission="masterdata:update">
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="masterdata:delete">
                        <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} disabled={record.status === "ACTIVE"}>
                            删除
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 处理表格分页变化
    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
    };

    // 处理搜索
    const handleSearch = (values) => {
        // 处理搜索参数，确保与后端API参数匹配
        const params = {};

        if (values.registration) {
            params.registration = values.registration;
        }

        if (values.aircraft_type) {
            params.aircraft_type = values.aircraft_type;
        }

        if (values.flight_number_offset) {
            params.flight_number_offset = values.flight_number_offset;
        }

        if (values.status) {
            params.status = values.status;
        }



        setFilterParams(params);
        setPagination({
            ...pagination,
            current: 1, // 重置到第一页
        });
    };

    // 重置搜索
    const handleReset = () => {
        setFilterParams({});
        setPagination({
            ...pagination,
            current: 1,
        });
    };

    // 添加飞机
    const handleAdd = () => {
        // 检查创建权限
        if (!hasPermission("masterdata:create")) {
            message.error("您没有创建飞机资源的权限");
            return;
        }

        setFormMode("add");
        setCurrentAircraft(null);
        setFormVisible(true);
    };

    // 编辑飞机
    const handleEdit = (record) => {
        // 检查更新权限
        if (!hasPermission("masterdata:update")) {
            message.error("您没有更新飞机资源的权限");
            return;
        }

        setFormMode("edit");
        setCurrentAircraft(record);
        setFormVisible(true);
    };

    const handleOpenAvailabilityDrawer = (record) => {
        if (!canReadAvailabilityWindows) {
            message.error("您没有查看飞机可用性窗口的权限");
            return;
        }
        setAvailabilityAircraft(record);
        setAvailabilityDrawerOpen(true);
    };

    // 删除飞机
    const handleDelete = (record) => {
        // 检查删除权限
        if (!hasPermission("masterdata:delete")) {
            message.error("您没有删除飞机资源的权限");
            return;
        }

        if (record.status === "ACTIVE") {
            message.warning("启用状态的飞机不能删除，请先停用");
            return;
        }

        confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除飞机 ${record.registration}（${record.aircraft_type}）？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                deleteMutation.mutate(record.id);
            },
        });
    };

    // 批量删除飞机
    const handleBatchDelete = () => {
        // 检查删除权限
        if (!hasPermission("masterdata:delete")) {
            message.error("您没有删除飞机资源的权限");
            return;
        }

        const activeAircraft = selectedRows.filter((item) => item.status === "ACTIVE");
        if (activeAircraft.length > 0) {
            message.warning("选择的飞机中包含启用状态的飞机，请先停用后再删除");
            return;
        }

        confirm({
            title: "确认批量删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedRowKeys.length} 架飞机吗？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                // 串行执行删除操作
                Promise.all(selectedRowKeys.map((key) => masterDataService.deleteAircraft(key)))
                    .then(() => {
                        message.success(`成功删除 ${selectedRowKeys.length} 架飞机`);
                        clearSelection();
                        refetch();
                    })
                    .catch((error) => {
                        message.error(`批量删除失败: ${error.message}`);
                    });
            },
        });
    };

    // 批量启用飞机
    const handleBatchEnable = () => {
        // 检查更新权限
        if (!hasPermission("masterdata:update")) {
            message.error("您没有更新飞机资源的权限");
            return;
        }

        // 这里假设有批量启用的API，实际情况可能需要调整
        message.info("批量启用功能正在开发中");
    };

    // 批量停用飞机
    const handleBatchDisable = () => {
        // 检查更新权限
        if (!hasPermission("masterdata:update")) {
            message.error("您没有更新飞机资源的权限");
            return;
        }

        // 这里假设有批量停用的API，实际情况可能需要调整
        message.info("批量停用功能正在开发中");
    };

    // 批量操作菜单项
    const batchActionItems = [
        {
            key: "delete",
            label: "批量删除",
            disabled: selectedRowKeys.length === 0 || !hasPermission("masterdata:delete"),
            onClick: handleBatchDelete,
        },
        {
            key: "enable",
            label: "批量启用",
            disabled: selectedRowKeys.length === 0 || !hasPermission("masterdata:update"),
            onClick: handleBatchEnable,
        },
        {
            key: "disable",
            label: "批量停用",
            disabled: selectedRowKeys.length === 0 || !hasPermission("masterdata:update"),
            onClick: handleBatchDisable,
        },
    ];

    // 表单提交成功
    const handleFormSuccess = () => {
        setFormVisible(false);
        refetch();
    };

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">飞机资源管理</div>
                <div className="page-action-compact">
                    <PermissionGuard permission="masterdata:create">
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            添加飞机
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission={["masterdata:update", "masterdata:delete"]} requireAll={false}>
                        <Dropdown menu={{ items: batchActionItems }} disabled={selectedRowKeys.length === 0}>
                            <Button>
                                批量操作 <DownOutlined />
                            </Button>
                        </Dropdown>
                    </PermissionGuard>
                </div>
            </div>

            {/* 搜索过滤 */}
            <Card className="filter-card">
                <AircraftFilter onSearch={handleSearch} onReset={handleReset} />
            </Card>

            {/* 选中项统计 */}
            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info">
                    已选择 <span className="selected-count">{selectedRowKeys.length}</span> 项
                    <Button type="link" onClick={clearSelection}>
                        清空
                    </Button>
                </div>
            )}

            {/* 飞机列表 */}
            <Card className="data-card">
                <Table
                    ref={tableRef}
                    size="small"
                    rowSelection={hasPermission("masterdata:delete") || hasPermission("masterdata:update") ? rowSelection : null}
                    columns={columns}
                    dataSource={data?.items || []}
                    rowKey="registration"
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                    onRow={getRowProps}
                />
            </Card>

            {/* 添加/编辑飞机表单 */}
            {formVisible && <AircraftForm visible={formVisible} mode={formMode} initialValues={currentAircraft} onCancel={() => setFormVisible(false)} onSuccess={handleFormSuccess} />}
            <AircraftAvailabilityWindowDrawer
                aircraft={availabilityAircraft}
                open={availabilityDrawerOpen}
                onClose={() => setAvailabilityDrawerOpen(false)}
                canWrite={canWriteAvailabilityWindows}
                canDelete={canDeleteAvailabilityWindows}
            />
        </div>
    );
};

export default AircraftList;
